import Request from './Request'
import constant from '../constants/constant'

const DEFAULT_MESSAGE = 'What are my biggest opportunities this week?'

function makeHeaders() {
  const accessToken = localStorage.getItem('access_token')
  const headers = {
    'Content-Type': 'application/json',
  }

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`
  }

  return headers
}

function parseChunk(chunk) {
  return chunk
    .split('\n\n')
    .map((block) => block.trim())
    .filter(Boolean)
    .map((block) => {
      const lines = block.split('\n')
      const event = lines.find((line) => line.startsWith('event:'))?.slice(6).trim() || 'message'
      const dataLine = lines.find((line) => line.startsWith('data:'))?.slice(5).trim() || 'null'
      let data

      try {
        data = JSON.parse(dataLine)
      } catch (error) {
        data = dataLine
      }

      return { event, data }
    })
}

function parseBackendErrorDetails(rawText) {
  if (typeof rawText !== 'string') {
    return { message: '', errorCode: undefined }
  }

  const trimmed = rawText.trim()
  if (!trimmed) {
    return { message: '', errorCode: undefined }
  }

  try {
    const parsed = JSON.parse(trimmed)
    const detail = parsed?.detail

    if (typeof detail === 'string' && detail.trim()) {
      return {
        message: detail.trim(),
        errorCode: typeof parsed?.error_code === 'string' ? parsed.error_code.trim() || undefined : undefined,
      }
    }

    if (detail && typeof detail === 'object' && typeof detail.message === 'string' && detail.message.trim()) {
      return {
        message: detail.message.trim(),
        errorCode: typeof detail.error_code === 'string' ? detail.error_code.trim() || undefined : undefined,
      }
    }

    if (typeof parsed?.message === 'string' && parsed.message.trim()) {
      return {
        message: parsed.message.trim(),
        errorCode: typeof parsed?.error_code === 'string' ? parsed.error_code.trim() || undefined : undefined,
      }
    }
  } catch (error) {
    // plain-text response body
  }

  return { message: trimmed, errorCode: undefined }
}

function createOrbitError(message, errorCode) {
  const error = new Error(message)
  if (errorCode) {
    error.orbitErrorCode = errorCode
  }
  return error
}

function firstNonEmptyString(...values) {
  for (const value of values) {
    if (typeof value !== 'string') {
      continue
    }
    const trimmed = value.trim()
    if (trimmed) {
      return trimmed
    }
  }
  return undefined
}

function firstNonEmptyText(...values) {
  for (const value of values) {
    if (typeof value !== 'string') {
      continue
    }
    if (value.trim()) {
      return value
    }
  }
  return undefined
}

function inferContentTypeFromFilename(filename) {
  const normalized = String(filename || '').toLowerCase()
  if (normalized.endsWith('.csv')) return 'text/csv'
  if (normalized.endsWith('.json')) return 'application/json'
  if (normalized.endsWith('.pdf')) return 'application/pdf'
  if (normalized.endsWith('.xlsx')) return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  if (normalized.endsWith('.xls')) return 'application/vnd.ms-excel'
  return undefined
}

function normalizeUpload(upload) {
  if (!upload || typeof upload !== 'object') {
    return null
  }

  const metadata = upload.metadata && typeof upload.metadata === 'object' ? upload.metadata : {}
  const filename = firstNonEmptyString(
    upload.filename,
    metadata.filename,
    metadata.original_filename,
    metadata.name,
  )
  const contentType = firstNonEmptyString(
    upload.content_type,
    metadata.content_type,
    metadata.mime_type,
  ) || inferContentTypeFromFilename(filename)
  const text = firstNonEmptyText(upload.text, upload.content, metadata.text)

  if (!filename || !contentType || !text) {
    return null
  }

  return {
    filename,
    content_type: contentType,
    text,
    metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
  }
}

function buildPayload(client, draft = {}) {
  const context = draft?.context && typeof draft.context === 'object' ? draft.context : {}
  const metadata = draft?.metadata && typeof draft.metadata === 'object' ? draft.metadata : {}
  const uploads = Array.isArray(draft?.uploads)
    ? draft.uploads
      .map((upload) => normalizeUpload(upload))
      .filter(Boolean)
    : []

  return {
    message: String(draft?.message || '').trim() || DEFAULT_MESSAGE,
    conversation_id: draft?.conversation_id || undefined,
    idempotency_key: draft?.idempotency_key || undefined,
    client_code: client?.ClientCode,
    client_name: client?.ClientCode || client?.Name,
    client_display_name: client?.Name,
    context,
    metadata,
    uploads,
  }
}

async function post(clientId, path, draft) {
  return Request.post(`/api/clients/${clientId}/orbit/${path}`, draft)
}

async function fetchContextBootstrap(clientId, draft) {
  return post(clientId, 'context/bootstrap', draft)
}

async function refreshContext(clientId, draft) {
  return post(clientId, 'context/refresh', draft)
}

async function fetchContextSummary(clientId, draft) {
  return post(clientId, 'context/summary', draft)
}

async function fetchQuestionCatalog(clientId, draft) {
  return post(clientId, 'questions', draft)
}

async function fetchFollowupCatalog(clientId, questionId, draft) {
  return post(clientId, `questions/${questionId}/followups`, draft)
}

async function validateChat(clientId, draft) {
  return post(clientId, 'chat/validate', draft)
}

async function executeChat(clientId, draft) {
  return post(clientId, 'chat/execute', draft)
}

async function streamChat(clientId, draft, handlers = {}) {
  const response = await fetch(`${constant.BACKEND_DOMAIN}/api/clients/${clientId}/orbit/chat/stream`, {
    method: 'POST',
    headers: makeHeaders(),
    body: JSON.stringify(draft),
  })

  if (!response.ok || !response.body) {
    const text = await response.text()
    const { message, errorCode } = parseBackendErrorDetails(text)
    throw createOrbitError(message || 'Orbit AI stream request failed', errorCode)
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { value, done } = await reader.read()
    if (done) {
      break
    }

    buffer += decoder.decode(value, { stream: true })
    const parts = buffer.split('\n\n')
    buffer = parts.pop() || ''

    for (const part of parts) {
      for (const event of parseChunk(`${part}\n\n`)) {
        handlers.onEvent?.(event)
      }
    }
  }

  if (buffer.trim()) {
    for (const event of parseChunk(buffer)) {
      handlers.onEvent?.(event)
    }
  }

  handlers.onDone?.()
}

export default {
  DEFAULT_MESSAGE,
  buildPayload,
  fetchContextBootstrap,
  refreshContext,
  fetchContextSummary,
  fetchQuestionCatalog,
  fetchFollowupCatalog,
  validateChat,
  executeChat,
  streamChat,
}

